import { Card, Skeleton, Text } from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { useGetSpvDraftQuery } from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard, Progression, ProgressionStep } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { getDraftCurrentStep, isDraftEditable } from '../utils';

type Props = unknown;

export function SPVProgression(): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSPVProgression = useTranslations('SPVProgression');

  const { user } = useUser();

  const { id = '' } = useParams();

  const { data, loading, error } = useGetSpvDraftQuery({ variables: { id } });

  if (loading) {
    return (
      <Skeleton>
        <Card h="150px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="150px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="150px" />;
  }

  const draft = data.spvDraft;

  const editable = isDraftEditable(draft, user);
  const currentStep = getDraftCurrentStep(draft.status);

  return (
    <Card>
      <Progression currentStep={currentStep} totalSteps={5}>
        <ProgressionStep isCompleted title={i18nSPVProgression('Step1')}>
          {editable && (
            <Link to={routes.onboardings.spvEdit.fmt(draft?.id || '')}>
              <Text fontSize="xs" fontWeight="semibold" textDecoration="underline">
                {i18nGeneral('Modify')}
              </Text>
            </Link>
          )}
        </ProgressionStep>
        <ProgressionStep isCompleted={currentStep >= 2} title={i18nSPVProgression('Step2')} />
        <ProgressionStep isCompleted={currentStep >= 3} title={i18nSPVProgression('Step3')}>
          <Text color="gray.600" fontSize="xs">
            {i18nSPVProgression('SubTitle3')}
          </Text>
        </ProgressionStep>
        <ProgressionStep isCompleted={currentStep >= 4} title={i18nSPVProgression('Step4')}>
          <Text color="gray.600" fontSize="xs">
            {i18nSPVProgression('SubTitle4')}
          </Text>
        </ProgressionStep>
        <ProgressionStep isCompleted={currentStep >= 5} title={i18nSPVProgression('Step5')}>
          <Text color="gray.600" fontSize="xs">
            {i18nSPVProgression('SubTitle5')}
          </Text>
        </ProgressionStep>
      </Progression>
    </Card>
  );
}

export type SPVProgressionProps = Props;
